import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import ClearIcon from "@mui/icons-material/Clear";
import ErrorModal from "../../../../utils/ErrorModal";

const gstOptions = [0, 5, 12, 18, 28];

const WorkItems = ({ rfq, setStep, user }) => {
  const [units, setUnits] = useState();
  const [currency, setCurrency] = useState();
  const fileInputRef = useRef(null);
  const [btnLoading, setBtnLoading] = useState(false);
  const [itemLoading, setItemLoading] = useState(true);
  const [errorModal, setErrorModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState();
  const [fieldErrors, setFieldErrors] = useState({});
  const [dataLoading, setDataLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const handleFileSelect = () => {
    fileInputRef.current.click();
  };

  const [data, setData] = useState([
    {
      code: "",
      description: "",
      section: "",
      unit: "",
      quantity: "",
      rate: "",
      gst: null,
      poRate: "",
      poNumber: "",
      sapPr: "",
      total: 0,
    },
    {
      code: "",
      description: "",
      section: "",
      unit: "",
      quantity: "",
      rate: "",
      gst: null,
      poRate: "",
      poNumber: "",
      sapPr: "",
      total: 0,
    },
    {
      code: "",
      description: "",
      section: "",
      unit: "",
      quantity: "",
      rate: "",
      gst: null,
      poRate: "",
      poNumber: "",
      sapPr: "",
      total: 0,
    },
    {
      code: "",
      description: "",
      section: "",
      unit: "",
      quantity: "",
      rate: "",
      gst: null,
      poRate: "",
      poNumber: "",
      sapPr: "",
      total: 0,
    },
    {
      code: "",
      description: "",
      section: "",
      unit: "",
      quantity: "",
      rate: "",
      gst: null,
      poRate: "",
      poNumber: "",
      sapPr: "",
      total: 0,
    },
  ]);

  useEffect(() => {
    const getWorkItems = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/draft/work-item?draftRfqId=${rfq.draftRfqId}`,
          { headers: { Authorization: `Bearer ${user.accessToken}` } },
        );
        if (res.data.length > 0) {
          setDataLoading(true);
          setData(res.data);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setDataLoading(false);
      }
    };
    getWorkItems();
  }, []);

  useEffect(() => {
    const getUnits = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/unit`,
          { headers: { Authorization: `Bearer ${user.accessToken}` } },
        );
        setUnits(res.data);
        setItemLoading(false);
      } catch (error) {
        console.log(error);
      }
    };
    getUnits();
  }, []);

  useEffect(() => {
    const getCurrency = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/org-currency/get?username=${user.username}`,
          { headers: { Authorization: `Bearer ${user.accessToken}` } },
        );
        setCurrency(res.data);
      } catch (error) {
        console.log(error);
      }
    };
    getCurrency();
  }, []);

  const groupedUnits = units?.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  const unitOptions = groupedUnits
    ? Object.entries(groupedUnits).flatMap(([category, units]) =>
        units.map((unit) => ({
          ...unit,
          category, // Add the group for grouping
        })),
      )
    : [];

  const handleChange = (index, field, value) => {
    if (field === "quantity" || field === "rate") {
      const [intPart, decPart] = value.split(".");
      if (decPart?.length > 2) {
        value = intPart + "." + decPart.slice(0, 2);
      }
    }
    const updatedData = [...data];
    updatedData[index][field] = value;
    updatedData[index].total =
      updatedData[index].quantity * updatedData[index].rate;
    setData(updatedData);
    const updatedFieldErrors = { ...fieldErrors };

    if (updatedFieldErrors[index]?.[field]) {
      updatedFieldErrors[index] = {
        ...updatedFieldErrors[index],
        [field]: false,
      };
    }

    setFieldErrors(updatedFieldErrors);
  };

  const addRow = () => {
    setData([
      ...data,
      {
        code: "",
        description: "",
        section: "",
        unit: "",
        quantity: "",
        rate: "",
        gst: null,
        poRate: "",
        poNumber: "",
        sapPr: "",
        total: 0,
      },
    ]);
  };

  const deleteRow = (index) => {
    const updatedData = data.filter((_, i) => i !== index);
    setData(updatedData);
  };

  const exportToExcel = () => {
    const headers = [
      { label: "Item Code", key: "code" },
      { label: "Description", key: "description" },
      { label: "Item Group", key: "section" },
      { label: "UOM", key: "unit" },
      { label: "Quantity", key: "quantity" },
      { label: "Rate (in ₹)", key: "rate" },
      { label: "PO Rate (in ₹)", key: "poRate" },
      { label: "PO Number", key: "poNumber" },
      { label: "SAP PR", key: "sapPr" },
      { label: "GST (%)", key: "gst" },
      { label: "Total (in ₹)", key: "total" },
    ];

    // Format data rows according to backend keys
    const formattedData = data.map((row) => {
      const newRow = {};
      headers.forEach((header) => {
        newRow[header.label] = row[header.key]; // Display header label, fetch backend value
      });
      return newRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Work Items");
    XLSX.writeFile(workbook, "WorkItemsDetails.xlsx");
  };

  const importFromExcel = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setIsImporting(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      const bufferArray = e.target.result;
      const workbook = XLSX.read(bufferArray, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const parsedData = XLSX.utils.sheet_to_json(sheet);

      const headerMapping = {
        "Item Code": "code",
        Description: "description",
        "Item Group": "section",
        UOM: "unit",
        Quantity: "quantity",
        "Rate (in ₹)": "rate",
        "PO Rate (in ₹)": "poRate",
        "PO Number": "poNumber",
        "SAP PR": "sapPr",
        "GST (%)": "gst",
        "Total (in ₹)": "total",
      };

      const expectedHeaders = Object.keys(headerMapping);

      const actualHeaders = Object.keys(parsedData[0] || {}).map((h) =>
        h.trim(),
      );

      const missingHeaders = expectedHeaders.filter(
        (header) => !actualHeaders.includes(header),
      );

      if (missingHeaders.length > 0) {
        setErrorModal(true);
        setErrorMsg(
          `Please upload Excel in correct format. Missing headers: ${missingHeaders.join(
            ", ",
          )}`,
        );
        event.target.value = null;
        setIsImporting(false);
        return;
      }

      const validUnitNames = new Set(
        units.map((unit) => unit.shortform?.toLowerCase()),
      );
      const validGST = new Set(gstOptions.map((gst) => gst));

      let hasInvalidUnit = false;
      let hasInvalidGST = false;

      const updatedData = parsedData.map((row) => {
        // Map columns to backend keys
        const mappedRow = {};
        Object.keys(headerMapping).forEach((label) => {
          mappedRow[headerMapping[label]] = row[label];
        });

        const unitValue = mappedRow.unit?.toString().trim().toLowerCase();
        const isValidUnit = validUnitNames.has(unitValue);

        if (!isValidUnit) {
          hasInvalidUnit = true;
          mappedRow.unit = "";
        }

        const gstValue = parseFloat(mappedRow.gst);
        const isValidGST = validGST.has(gstValue);
        if (!isValidGST) {
          hasInvalidGST = true;
          mappedRow.gst = null;
        } else {
          mappedRow.gst = gstValue;
        }

        mappedRow.description =
          mappedRow.description?.toString().slice(0, 1000) || "";

        return {
          ...mappedRow,
          quantity: parseFloat(mappedRow.quantity).toFixed(2) || 0,
          // rate: parseFloat(mappedRow.rate).toFixed(2) || 0,
          rate:
            mappedRow.rate === undefined ||
            mappedRow.rate === null ||
            mappedRow.rate === ""
              ? ""
              : Number(parseFloat(mappedRow.rate).toFixed(2)),
          // poRate: parseFloat(mappedRow.poRate).toFixed(2) || 0,
          poRate:
            mappedRow.poRate === undefined ||
            mappedRow.poRate === null ||
            mappedRow.poRate === ""
              ? ""
              : Number(parseFloat(mappedRow.poRate).toFixed(2)),
          gst:
            mappedRow.gst !== undefined && mappedRow.gst !== null
              ? parseFloat(mappedRow.gst)
              : 0,
          total:
            (parseFloat(mappedRow.quantity).toFixed(2) || 0) *
            (mappedRow.rate === undefined ||
            mappedRow.rate === null ||
            mappedRow.rate === ""
              ? 1
              : Number(parseFloat(mappedRow.rate).toFixed(2))),
        };
      });

      setData(updatedData);
      setIsImporting(false);
      event.target.value = null;
    };
    reader.readAsArrayBuffer(file);
  };

  const grandTotal = data
    .reduce((sum, row) => sum + parseFloat(row.total || 0), 0)
    .toFixed(2);

  const validate = (filteredData) => {
    const newFieldErrors = {};

    filteredData?.forEach((item, index) => {
      const errors = {};

      const anyCodeFilled = filteredData.some(
        (item) => item.code?.toString().trim() !== "",
      );

      if (!item.description?.trim()) errors.description = true;
      if (!item.unit?.trim()) errors.unit = true;
      if (!item.quantity || Number(item.quantity) <= 0) errors.quantity = true;
      if (item.gst === null || item.gst === undefined || Number(item.gst) < 0)
        errors.gst = true;
      // if (!item.rate || Number(item.rate) <= 0) errors.rate = true;

      if (anyCodeFilled && (!item.code || item.code.toString().trim() === "")) {
        errors.code = true;
      }

      if (Object.keys(errors).length > 0) {
        newFieldErrors[index] = errors;
      }
    });

    setFieldErrors(newFieldErrors);
    return Object.keys(newFieldErrors).length === 0;
  };

  const handleNext = async () => {
    const filteredData = data.filter((item) => {
      const isEmptyRow =
        // (item.code?.toString().trim() === "" || item.code === undefined) &&
        (item.description?.toString().trim() === "" ||
          item.description === undefined) &&
        // (item.section?.toString().trim() === "" ||
        //   item.section === undefined) &&
        (item.unit?.toString().trim() === "" || item.unit === undefined) &&
        (item.gst === null || item.gst === undefined || item.gst < 0) &&
        (item.quantity === null ||
          item.quantity === undefined ||
          Number(item.quantity) <= 0);
      // (item.rate === null ||
      //   item.rate === undefined ||
      //   Number(item.rate) <= 0);
      return !isEmptyRow;
    });

    // if (!validate(filteredData)) {
    //   return;
    // }

    const anyIncompleteRow = filteredData.some((item) => {
      const isFilled =
        // item.code?.toString().trim() !== "" &&
        item.description?.toString().trim() !== "" &&
        // item.section?.toString().trim() !== "" &&
        item.unit?.toString().trim() !== "" &&
        item.gst >= 0 &&
        item.quantity > 0;
      // item.rate > 0;

      return !isFilled;
    });

    const anyCodeFilled = filteredData.some(
      (item) => item.code?.toString().trim() !== "",
    );
    const anyCodeMissing = filteredData.some(
      (item) => !item.code || item.code.toString().trim() === "",
    );

    if (anyCodeFilled && anyCodeMissing) {
      setErrorModal(true);
      setErrorMsg(
        "You've entered an item code for one or more rows. Please enter item codes for all rows or leave them all empty to auto-generate.",
      );
      if (!validate(filteredData)) {
        return;
      }
      return;
    }

    if (anyIncompleteRow) {
      setErrorModal(true);
      setErrorMsg(
        "Please check if you have:\n 1. Added atleast one row with complete details.\n2. Filled all the details for every item.\n3. All quantities are above 0.\n4. Selected all units properly.",
      );
      if (!validate(filteredData)) {
        return;
      }
      return;
    }

    // DUPLICATE ROW CHECK
    const seenRows = new Set();
    const duplicateRows = [];

    filteredData.forEach((item, index) => {
      const key =
        item.code?.toString().trim().toLowerCase() +
        "|" +
        item.description?.toString().trim().toLowerCase() +
        "|" +
        item.unit?.toString().trim().toLowerCase() +
        "|" +
        Number(item.gst) +
        "|" +
        Number(item.quantity);

      if (seenRows.has(key)) {
        duplicateRows.push(index + 1);
      } else {
        seenRows.add(key);
      }
    });

    if (duplicateRows.length > 0) {
      setErrorModal(true);
      setErrorMsg(
        `Duplicate item rows detected.\nEach row must be unique.\nDuplicate rows found at positions: ${duplicateRows.join(
          ", ",
        )}`,
      );
      return;
    }

    if (filteredData.length === 0) {
      setErrorModal(true);
      setErrorMsg(
        "Please check if you have:\n 1. Added atleast one row with complete details.\n2. Filled all the details for every item.\n3. All quantities are above 0.\n4. Selected all units properly.",
      );
      return;
    }
    setBtnLoading(true);
    try {
      const indexedData = filteredData.map((item, index) => ({
        ...item,
        description: item?.description.slice(0, 2000),
        section: item?.section || "Uncategorised",
        rate: item?.rate || 1,
        poRate: item?.poRate || 0,
        itemOrder: index,
      }));
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/draft/work-item?draftRfqId=${rfq.draftRfqId}`,
        indexedData,
        { headers: { Authorization: `Bearer ${user.accessToken}` } },
      );
      setStep((prev) => prev + 1);
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
  };
  return (
    <div style={{ position: "relative" }}>
      {isImporting && (
        <div className="uploading-div loading-div">
          Uploading Sheet... <CircularProgress color="#fff" />
        </div>
      )}
      {btnLoading && <div className="loading-div"></div>}
      <h2>Work Items Details</h2>
      <div className="dashboard-container">
        <table
          border={1}
          style={{
            borderCollapse: "collapse",
            maxWidth: "100%",
            marginTop: "10px",
          }}
        >
          <thead>
            <tr>
              <th style={{ padding: "20px" }}>Item Code</th>
              <th>Description</th>
              <th>Item Group</th>
              <th>UOM</th>
              <th>Quantity</th>
              <th style={{ width: "100px" }}>Rate (in &#8377;)</th>
              <th style={{ width: "100px" }}>PO Rate (in &#8377;)</th>
              <th>PO Number</th>
              <th>SAP PR</th>
              <th>GST (in %)</th>
              <th>Total (in &#8377;)</th>
            </tr>
          </thead>
          <tbody>
            {dataLoading ? (
              <Box>Loading...</Box>
            ) : itemLoading ? (
              <Box>Loading...</Box>
            ) : (
              data
                .sort((a, b) => a.itemOrder - b.itemOrder)
                .map((row, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        className={`table-input ${
                          fieldErrors[index]?.code && "input-error"
                        }`}
                        type="text"
                        value={row.code}
                        onChange={(e) =>
                          handleChange(index, "code", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        className={`table-input ${
                          fieldErrors[index]?.description && "input-error"
                        }`}
                        type="text"
                        value={row.description}
                        onChange={(e) =>
                          handleChange(index, "description", e.target.value)
                        }
                        maxLength={2000}
                      />
                    </td>
                    <td>
                      <input
                        className="table-input"
                        type="text"
                        value={row.section}
                        onChange={(e) =>
                          handleChange(index, "section", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <Autocomplete
                        className={`unit-dropdown ${
                          fieldErrors[index]?.unit && "input-error"
                        }`}
                        options={unitOptions}
                        getOptionLabel={(option) =>
                          `${option.title} (${option.shortform})`
                        }
                        groupBy={(option) => option.category}
                        value={
                          unitOptions.find(
                            (opt) => opt.shortform === row.unit,
                          ) || null
                        }
                        onChange={(event, newValue) =>
                          handleChange(
                            index,
                            "unit",
                            newValue ? newValue.shortform : "",
                          )
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.shortform === value.shortform
                        }
                        disablePortal
                        size="small"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Choose Unit"
                            variant="outlined"
                            size="small"
                            sx={{
                              "& .MuiInputBase-root": {
                                minHeight: "45px",
                                fontSize: "12px",
                              },
                              "& input": {
                                padding: "15px",
                              },
                            }}
                          />
                        )}
                        filterSelectedOptions
                        autoHighlight
                        selectOnFocus
                        clearOnBlur={false}
                        handleHomeEndKeys
                        disableClearable
                        sx={{ width: "170px" }}
                        slotProps={{
                          paper: {
                            sx: {
                              background: "#D8E9F0",
                              "& .MuiAutocomplete-option": {
                                fontSize: "12px",
                                padding: "6px 12px",
                              },
                              "& .MuiAutocomplete-groupLabel": {
                                fontWeight: "bold",
                                color: "#000",
                                fontSize: "13px",
                                backgroundColor: "#D8E9F0",
                                padding: "0px 6px",
                                lineHeight: "2",
                                position: "static",
                              },
                            },
                          },
                        }}
                      />
                    </td>
                    <td>
                      <input
                        className={`table-input ${
                          fieldErrors[index]?.quantity && "input-error"
                        }`}
                        type="number"
                        value={row.quantity}
                        style={{ width: "80px" }}
                        onChange={(e) =>
                          handleChange(index, "quantity", e.target.value)
                        }
                        step={0.01}
                      />
                    </td>
                    <td>
                      <input
                        className={`table-input ${
                          fieldErrors[index]?.rate && "input-error"
                        }`}
                        type="number"
                        value={row.rate}
                        onChange={(e) =>
                          handleChange(index, "rate", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        className={`table-input`}
                        type="number"
                        value={row.poRate}
                        onChange={(e) =>
                          handleChange(index, "poRate", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        className={`table-input`}
                        type="text"
                        value={row.poNumber}
                        onChange={(e) =>
                          handleChange(index, "poNumber", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        className={`table-input`}
                        type="text"
                        value={row.sapPr}
                        onChange={(e) =>
                          handleChange(index, "sapPr", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <Autocomplete
                        className={`unit-dropdown ${
                          fieldErrors[index]?.gst && "input-error"
                        }`}
                        options={gstOptions}
                        getOptionLabel={(option) => `${option}%`}
                        value={
                          gstOptions.find((opt) => opt === row.gst) ?? null
                        }
                        onChange={(event, newValue) =>
                          handleChange(index, "gst", newValue ? newValue : 0)
                        }
                        isOptionEqualToValue={(option, value) =>
                          option === value
                        }
                        disablePortal
                        size="small"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Choose GST"
                            variant="outlined"
                            size="small"
                            sx={{
                              "& .MuiInputBase-root": {
                                minHeight: "45px",
                                fontSize: "12px",
                              },
                              "& input": {
                                padding: "15px",
                              },
                            }}
                          />
                        )}
                        filterSelectedOptions
                        autoHighlight
                        selectOnFocus
                        clearOnBlur={false}
                        handleHomeEndKeys
                        disableClearable
                        sx={{ width: "100px" }}
                        slotProps={{
                          paper: {
                            sx: {
                              background: "#D8E9F0",
                              "& .MuiAutocomplete-option": {
                                fontSize: "12px",
                                padding: "6px 12px",
                              },
                              "& .MuiAutocomplete-groupLabel": {
                                fontWeight: "bold",
                                color: "#000",
                                fontSize: "13px",
                                backgroundColor: "#D8E9F0",
                                padding: "0px 6px",
                                lineHeight: "2",
                                position: "static",
                              },
                            },
                          },
                        }}
                      />
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {row.total.toFixed(2)}
                    </td>
                    <td>
                      {/* <Tooltip title="Remove Row"> */}
                      <Button
                        className="btn-contained"
                        onClick={() => deleteRow(index)}
                        sx={{
                          margin: "0 !important",
                          borderRadius: "5px !important",
                          minWidth: "50px !important",
                          width: "0",
                          "&:hover": { background: "#EB2B0B !important" },
                        }}
                      >
                        <ClearIcon />
                      </Button>
                      {/* </Tooltip> */}
                    </td>
                  </tr>
                ))
            )}

            <tr style={{ backgroundColor: "#d8e9f0", fontWeight: "bold" }}>
              <td
                colSpan={10}
                style={{ textAlign: "right", paddingRight: "10px" }}
              >
                Grand Total (in &#8377;):
              </td>
              <td
                style={{ width: "150px", textAlign: "center", padding: "20px" }}
              >
                {grandTotal}
              </td>
            </tr>
          </tbody>
        </table>
        <Typography variant="p" fontWeight={700} fontSize={"14px"}>
          [Important Note: <br />
          - Uploading Sheet will overwrite existing work items. <br />- Mention
          only UOM shortform in small case in the UOM column while uploading
          from excel sheet. <br />- Mention GST rates without percentage (%)
          symbol in the GST column while uploading from excel sheet. <br />-
          Please ensure that the Description field contains less than 1000
          characters. Any additional characters beyond this limit will be
          automatically truncated during processing. Kindly review and adjust
          your input accordingly to avoid data loss.]
        </Typography>
        <div
          style={{
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            gap: 10,
          }}
        >
          <Button className="btn-outlined" onClick={handleFileSelect}>
            Upload Excel
          </Button>
          <Button
            className="btn-outlined"
            onClick={addRow}
            sx={{ padding: "5px 40px !important" }}
          >
            Add Work Item
          </Button>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "end",
          }}
        >
          <button
            onClick={exportToExcel}
            style={{
              cursor: "pointer",
              border: "none",
              background: "none",
              borderBottom: "1px solid #000",
            }}
          >
            Download Template Excel
          </button>
        </div>
      </div>
      <input
        type="file"
        accept=".xlsx, .xls"
        ref={fileInputRef}
        onChange={importFromExcel}
        style={{ display: "none" }}
      />
      <div style={{ display: "flex", justifyContent: "end" }}>
        <div
          className="btn-container"
          style={{ gap: 20, margin: "0", justifyContent: "end" }}
        >
          <Button className="btn-outlined" onClick={handleBack}>
            Back
          </Button>
          <Button
            className="btn-contained"
            onClick={handleNext}
            disabled={btnLoading}
          >
            {btnLoading ? "Submitting..." : "Next"}
          </Button>
        </div>
      </div>

      <ErrorModal
        open={errorModal}
        handleClose={() => setErrorModal(false)}
        msg={errorMsg}
      />
    </div>
  );
};

export default WorkItems;
